/**
 * F14.1 — OPERACIONES OFFLINE DE LIMPIEZA Y CALIDAD (las que ya existían desde FASE 5)
 *
 * Declaración de los ocho tipos en el registro común (`operaciones.ts`): las cuatro acciones de línea y las tres
 * transiciones de NC viajan en el lote de `POST /api/sync`; la evidencia tiene transporte propio (multipart). El
 * comportamiento es EXACTAMENTE el de FASE 5.3–5.7 (mismas reconciliaciones, mismas reglas de dependencia): sólo ha
 * cambiado dónde vive, para que el motor no conozca ningún dominio.
 */
import { api, FalloApi } from '../api.ts';
import {
  actualizarLineaTrasSincronizacion,
  eliminarBlobEvidencia,
  leerBlobEvidencia,
  leerMutacion,
  resolverMutacionTrasSincronizacion,
  type Mutacion,
  type PayloadEvidenciaSubir,
  type TipoMutacion,
} from './db.ts';
import { cabeceraPerfilOffline, CODIGO_PERFIL_DE_OTRO_USUARIO } from './identidad-sync.ts';
import { type DatosAplicada, registrarOperacion, type ResultadoAplicadoLote, type ResumenParcial } from './operaciones.ts';

// ---------------------------------------------------------------- líneas y NC (lote)

/** Línea: el servidor devuelve el evento aplicado; la línea local pasa a su estado nuevo y revisión. */
async function reconciliarLinea(idPerfil: string, _m: Mutacion, r: ResultadoAplicadoLote): Promise<void> {
  const evento = r['evento'] as { idLinea: string; estadoNuevo: string };
  await actualizarLineaTrasSincronizacion(idPerfil, evento.idLinea, { estado: evento.estadoNuevo, revisionBase: r.revisionServidorActual });
}

/** NC: se guarda el resultado (id real, código, estado y la revisión que deja) en la propia operación. */
function datosNc(_m: Mutacion, r: ResultadoAplicadoLote): DatosAplicada {
  const nc = r['nc'] as { idNc: string; codigo: string; estado: string };
  //  `idHistorial` = `revisionServidorActual` (PRE-COMMIT REVIEW 5.4 punto 1): sin esto, una transición posterior
  //  sobre esta NC en otra sesión offline no tendría `revisionBase` real que enviar.
  return { resultadoNc: { idNc: nc.idNc, codigo: nc.codigo, estado: nc.estado, idHistorial: r.revisionServidorActual } };
}

const motivoObligatorio = (payload: Mutacion['payload']): string | null =>
  typeof (payload as { motivo?: unknown }).motivo === 'string' && (payload as { motivo: string }).motivo.trim() !== '' ? null : 'Falta el motivo.';

//  F14.2 — los mismos cuatro tipos para ENCARGADO y OPERARIO (nunca uno por rol); el servidor decide el modo
//  (gestión o ejecución propia) con la identidad de quien sincroniza.
const LINEAS: readonly [TipoMutacion, string, ((p: Mutacion['payload']) => string | null) | undefined][] = [
  ['LINEA_CONFIRMAR', 'Confirmar limpieza', undefined],
  ['LINEA_VERIFICAR', 'Verificar limpieza', undefined],
  ['LINEA_NO_OK', 'No conforme', motivoObligatorio],
  ['LINEA_NO_APLICA', 'No aplica', motivoObligatorio],
];
for (const [tipo, etiqueta, validarLocal] of LINEAS) {
  registrarOperacion({
    tipo, etiqueta, dominio: 'LIMPIEZA', versionPayload: 1, transporte: 'LOTE', reconciliarAplicada: reconciliarLinea,
    ...(validarLocal === undefined ? {} : { validarLocal }),
  });
}
registrarOperacion({ tipo: 'NC_CREAR', etiqueta: 'No conformidad (nueva)', dominio: 'CALIDAD', versionPayload: 1, transporte: 'LOTE', datosAplicada: datosNc });
registrarOperacion({ tipo: 'NC_INICIAR_TRATAMIENTO', etiqueta: 'No conformidad (en tratamiento)', dominio: 'CALIDAD', versionPayload: 1, transporte: 'LOTE', datosAplicada: datosNc });
registrarOperacion({ tipo: 'NC_CERRAR', etiqueta: 'No conformidad (cierre)', dominio: 'CALIDAD', versionPayload: 1, transporte: 'LOTE', datosAplicada: datosNc });

// ==================================================== evidencias (FASE 5.4 §10-§16), transporte propio

/** Mismo criterio que `sync/servicio.ts::CODIGOS_TRANSITORIOS` en el backend — nunca ERROR_REINTENTABLE para un rechazo de negocio. */
const CODIGOS_TRANSITORIOS = new Set(['REINTENTA', 'TIEMPO_AGOTADO', 'ERROR_INTERNO']);

export function rutaEvidencia(m: Mutacion, payload: PayloadEvidenciaSubir, idNcReal: string | null): string {
  if (payload.ambito === 'OT') return `/api/ots/${m.idEntidad}/evidencias`;
  //  F14.3 — foto de una intervención de mantenimiento: la misma ruta que en línea; `idNcReal` es aquí la intervención.
  if (payload.ambito === 'PT') {
    return `/api/mantenimiento/partes/${m.idEntidad}/evidencias${idNcReal === null ? '' : `?idIntervencion=${idNcReal}`}`;
  }
  //  F14.2 — la foto del operario va por SU ruta (la misma que en línea: sólo sus líneas, siempre INTERNA).
  if (payload.ambito === 'LINEA' && payload.rutaPropia === true) return `/api/mis-ots/${m.idOt}/lineas/${m.idEntidad}/evidencias`;
  if (payload.ambito === 'LINEA') return `/api/ots/${m.idOt}/lineas/${m.idEntidad}/evidencias`;
  return `/api/no-conformidades/${String(idNcReal)}/evidencias`;
}

/**
 * Resuelve, para UNA evidencia con ámbito NC, el `idNc` real a partir de la operación de la que depende:
 *   · LISTA — la dependencia ya se resolvió, subir ya;
 *   · ESPERA — la dependencia sigue activa; no se toca nada, se reintenta en la próxima ronda;
 *   · BLOQUEADA_DURABLE — la dependencia quedó RECHAZADA o EN_ESPERA: EN_ESPERA/DEPENDENCIA_BLOQUEADA, nunca
 *     RECHAZADA (el padre aún puede resolverse y la evidencia reencadenarse, FASE 5.5 §13/§14);
 *   · IMPOSIBLE — la dependencia ya no existe localmente: RECHAZADA.
 */
export async function resolverDependenciaEvidenciaNc(
  idPerfil: string,
  m: Mutacion,
): Promise<
  | { readonly estado: 'LISTA'; readonly idNc: string }
  | { readonly estado: 'ESPERA' }
  | { readonly estado: 'BLOQUEADA_DURABLE'; readonly motivo: string }
  | { readonly estado: 'IMPOSIBLE'; readonly motivo: string }
> {
  if (m.dependeDe === null) return { estado: 'LISTA', idNc: m.idEntidad };
  const dependencia = await leerMutacion(idPerfil, m.dependeDe);
  if (dependencia === null) {
    return { estado: 'IMPOSIBLE', motivo: 'La no conformidad de la que depende esta evidencia ya no está disponible en este dispositivo.' };
  }
  if (dependencia.resultadoNc != null) return { estado: 'LISTA', idNc: dependencia.resultadoNc.idNc };
  if (dependencia.estadoSync === 'RECHAZADA') {
    return { estado: 'BLOQUEADA_DURABLE', motivo: 'La no conformidad de la que depende esta evidencia fue rechazada por el servidor; revísala antes de continuar.' };
  }
  if (dependencia.estadoSync === 'EN_ESPERA') {
    return { estado: 'BLOQUEADA_DURABLE', motivo: 'La acción de la que depende esta evidencia necesita revisión antes de poder continuar.' };
  }
  return { estado: 'ESPERA' };
}

/**
 * F14.3 — lo mismo para una foto de mantenimiento cuya intervención se inició sin red: la dependencia es su
 * `MNT_INICIAR`, y está LISTA cuando el servidor devolvió el id real de la intervención (`resultadoMnt`). Se devuelve
 * en `idNc` para compartir el camino de subida (es el destino real de la foto, no una NC).
 */
export async function resolverDependenciaEvidenciaPt(
  idPerfil: string,
  m: Mutacion,
): ReturnType<typeof resolverDependenciaEvidenciaNc> {
  if (m.dependeDe === null) return { estado: 'IMPOSIBLE', motivo: 'Esta foto no sabe a qué intervención pertenece.' };
  const dependencia = await leerMutacion(idPerfil, m.dependeDe);
  if (dependencia === null) {
    return { estado: 'IMPOSIBLE', motivo: 'La intervención de esta foto ya no está disponible en este dispositivo.' };
  }
  const idIntervencion = dependencia.resultadoMnt?.idIntervencion ?? null;
  if (idIntervencion !== null) return { estado: 'LISTA', idNc: idIntervencion };
  if (dependencia.estadoSync === 'RECHAZADA' || dependencia.estadoSync === 'EN_ESPERA') {
    return { estado: 'BLOQUEADA_DURABLE', motivo: 'El inicio de la intervención de esta foto necesita revisión antes de poder continuar.' };
  }
  return { estado: 'ESPERA' };
}

/** Una evidencia: todo dentro de un `try` (una foto que falla nunca tumba la ronda de las demás). */
async function subirUnaEvidencia(idPerfil: string, m: Mutacion, resumen: ResumenParcial): Promise<void> {
  try {
    const payload = m.payload as PayloadEvidenciaSubir;

    let idNcReal: string | null = null;
    if (payload.ambito === 'PT' && m.dependeDe === null) idNcReal = payload.idIntervencion ?? null;
    if (payload.ambito === 'NC' || (payload.ambito === 'PT' && m.dependeDe !== null)) {
      //  F14.3 — la foto de una intervención iniciada sin red espera a su `MNT_INICIAR` con las mismas reglas que una
      //  foto de NC creada sin red (lista / espera / bloqueada / imposible).
      const destino = payload.ambito === 'NC' ? await resolverDependenciaEvidenciaNc(idPerfil, m) : await resolverDependenciaEvidenciaPt(idPerfil, m);
      if (destino.estado === 'ESPERA') return;
      if (destino.estado === 'BLOQUEADA_DURABLE') {
        await resolverMutacionTrasSincronizacion(idPerfil, m.idOperacionCliente, {
          estadoSync: 'EN_ESPERA',
          ultimoError: { codigo: 'DEPENDENCIA_NO_APLICADA', mensaje: destino.motivo },
          razonEspera: 'DEPENDENCIA_BLOQUEADA',
        });
        resumen.omitidas += 1;
        return;
      }
      if (destino.estado === 'IMPOSIBLE') {
        await resolverMutacionTrasSincronizacion(idPerfil, m.idOperacionCliente, {
          estadoSync: 'RECHAZADA',
          ultimoError: { codigo: 'DEPENDENCIA_NO_APLICADA', mensaje: destino.motivo },
        });
        resumen.rechazadas += 1;
        return;
      }
      idNcReal = destino.idNc;
    }

    const blob = await leerBlobEvidencia(idPerfil, m.idOperacionCliente);
    if (blob === null) {
      await resolverMutacionTrasSincronizacion(idPerfil, m.idOperacionCliente, {
        estadoSync: 'RECHAZADA',
        ultimoError: { codigo: 'EVIDENCIA_LOCAL_PERDIDA', mensaje: 'El fichero ya no está disponible en este dispositivo.' },
      });
      resumen.rechazadas += 1;
      return;
    }

    const formulario = new FormData();
    formulario.set('archivo', blob, payload.nombreArchivo);
    formulario.set('contexto', payload.contexto);
    formulario.set('visibilidad', payload.visibilidad);
    formulario.set('idOperacionCliente', m.idOperacionCliente);
    if (payload.descripcion !== null) formulario.set('descripcion', payload.descripcion);
    if (payload.tomadaEn !== null) formulario.set('tomadaEn', payload.tomadaEn);

    let respuesta: { readonly id: string; readonly estado: string; readonly yaEstaba: boolean };
    try {
      respuesta = await api.subir(rutaEvidencia(m, payload, idNcReal), formulario, { headers: cabeceraPerfilOffline(idPerfil) });
    } catch (e) {
      //  F14.2 — el servidor dice que este perfil no es el de la sesión: nada cambia, la foto sigue siendo de su dueño.
      if (e instanceof FalloApi && e.codigo === CODIGO_PERFIL_DE_OTRO_USUARIO) return;
      //  `FalloApi(0, SIN_CONEXION)` = no hubo respuesta: igual que un fallo de red en `/api/sync`, no se toca nada.
      if (e instanceof FalloApi && e.codigo !== 'SIN_CONEXION') {
        if (CODIGOS_TRANSITORIOS.has(e.codigo) || e.estado >= 500 || e.estado === 429) {
          await resolverMutacionTrasSincronizacion(idPerfil, m.idOperacionCliente, {
            estadoSync: 'ERROR_REINTENTABLE',
            ultimoError: { codigo: e.codigo, mensaje: e.message },
            incrementarIntentos: true,
          });
          resumen.errorTemporal += 1;
        } else if (e.estado !== 401) {
          //  Un 401 (sesión caducada) nunca convierte la evidencia en rechazada: se queda como estaba hasta volver a
          //  entrar. Cualquier otro 4xx es un rechazo de negocio.
          await resolverMutacionTrasSincronizacion(idPerfil, m.idOperacionCliente, {
            estadoSync: 'RECHAZADA',
            ultimoError: { codigo: e.codigo, mensaje: e.message },
          });
          resumen.rechazadas += 1;
        }
      }
      return;
    }

    await resolverMutacionTrasSincronizacion(idPerfil, m.idOperacionCliente, { estadoSync: 'SINCRONIZADA' });
    //  §16 — el blob local sólo se borra tras una confirmación real del servidor.
    await eliminarBlobEvidencia(idPerfil, m.idOperacionCliente);
    if (respuesta.yaEstaba) resumen.yaAplicadas += 1;
    else resumen.aplicadas += 1;
  } catch (e) {
    await resolverMutacionTrasSincronizacion(idPerfil, m.idOperacionCliente, {
      estadoSync: 'ERROR_REINTENTABLE',
      ultimoError: { codigo: 'ERROR_INESPERADO', mensaje: e instanceof Error ? e.message : 'Fallo inesperado al preparar la subida.' },
      incrementarIntentos: true,
    });
    resumen.errorTemporal += 1;
  }
}

registrarOperacion({ tipo: 'EVIDENCIA_SUBIR', etiqueta: 'Evidencia', dominio: 'COMUN', versionPayload: 1, transporte: 'PROPIO', sincronizar: subirUnaEvidencia });
